import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Play, TrendingUp, TrendingDown, DollarSign, Target, AlertTriangle,
  RefreshCw, X, ChevronUp, ChevronDown, Clock, Percent, Award,
  BarChart3, Wallet, ArrowUpRight, ArrowDownRight, Loader2, Info, Lock, Globe
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { API, getAuthHeaders } from '../utils/api';

const MARKETS = ['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY', 'BTCUSD'];

export default function DemoTrading() {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState({});
  const [signals, setSignals] = useState([]);
  const [openTradeDialog, setOpenTradeDialog] = useState(false);
  const [closingTradeId, setClosingTradeId] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [demoAvailable, setDemoAvailable] = useState(true);
  const [marketStatus, setMarketStatus] = useState(null);
  
  // New trade form
  const [newTrade, setNewTrade] = useState({
    market: 'XAUUSD',
    direction: 'buy',
    entry_price: '',
    stop_loss: '',
    take_profit: '',
    lot_size: '0.01',
    signal_id: ''
  });

  const priceIntervalRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchPrices();
    
    // Update prices every 10 seconds
    priceIntervalRef.current = setInterval(fetchPrices, 10000);
    
    return () => {
      if (priceIntervalRef.current) {
        clearInterval(priceIntervalRef.current);
      }
    };
  }, []);

  // Update open trades with current prices
  useEffect(() => {
    if (Object.keys(prices).length > 0 && openTrades.length > 0) {
      updateTradesWithPrices();
    }
  }, [prices]);

  const fetchData = async () => {
    try {
      const [accountRes, openRes, closedRes, signalsRes] = await Promise.all([
        axios.get(`${API}/demo/account`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/demo/trades?status=open`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/demo/trades?status=closed`, { withCredentials: true, headers: getAuthHeaders() }),
        axios.get(`${API}/signals?status=active`, { withCredentials: true, headers: getAuthHeaders() })
      ]);
      
      setAccount(accountRes.data);
      setDemoAvailable(accountRes.data.demo_available !== false);
      setMarketStatus(accountRes.data.market_status);
      setOpenTrades(openRes.data.trades || []);
      setClosedTrades(closedRes.data.trades || []);
      const signalsData = signalsRes.data.signals || signalsRes.data;
      setSignals(Array.isArray(signalsData) ? signalsData : []);
    } catch (error) {
      console.error('Failed to fetch demo data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/real-prices`, { withCredentials: true, headers: getAuthHeaders() });
      if (response.data && response.data.markets) {
        const priceMap = {};
        response.data.markets.forEach(m => {
          priceMap[m.name] = m.raw_price;
        });
        setPrices(priceMap);
      }
    } catch (error) {
      console.error('Failed to fetch prices:', error);
    }
  };

  const updateTradesWithPrices = async () => {
    for (const trade of openTrades) {
      const currentPrice = prices[trade.market];
      if (currentPrice && currentPrice !== trade.current_price) {
        try {
          const response = await axios.post(
            `${API}/demo/trades/${trade.trade_id}/update-price?current_price=${currentPrice}`,
            {},
            { withCredentials: true, headers: getAuthHeaders() }
          );
          
          if (response.data.status === 'closed') {
            toast.info(`Trade ${trade.market} ${response.data.reason} hit! P&L: $${response.data.pnl}`);
            fetchData();
          }
        } catch (error) {
          console.error('Failed to update trade price:', error);
        }
      }
    }
  };

  const handleOpenTrade = async () => {
    if (!newTrade.entry_price || !newTrade.stop_loss || !newTrade.take_profit) {
      toast.error('Please fill in all required fields');
      return;
    }

    setProcessing(true);
    try {
      await axios.post(`${API}/demo/trades/open`, {
        market: newTrade.market,
        direction: newTrade.direction,
        entry_price: parseFloat(newTrade.entry_price),
        stop_loss: parseFloat(newTrade.stop_loss),
        take_profit: parseFloat(newTrade.take_profit),
        lot_size: parseFloat(newTrade.lot_size),
        signal_id: newTrade.signal_id || null
      }, { withCredentials: true, headers: getAuthHeaders() });
      
      toast.success('Demo trade opened successfully!');
      setOpenTradeDialog(false);
      setNewTrade({
        market: 'XAUUSD',
        direction: 'buy',
        entry_price: '',
        stop_loss: '',
        take_profit: '',
        lot_size: '0.01',
        signal_id: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open trade');
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseTrade = async (tradeId) => {
    const trade = openTrades.find(t => t.trade_id === tradeId);
    if (!trade) return;

    const currentPrice = prices[trade.market] || trade.current_price;
    
    setClosingTradeId(tradeId);
    try {
      const response = await axios.post(
        `${API}/demo/trades/${tradeId}/close`,
        { exit_price: currentPrice },
        { withCredentials: true }
      );
      
      toast.success(`Trade closed! P&L: $${response.data.pnl}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close trade');
    } finally {
      setClosingTradeId(null);
    }
  };

  const handleResetAccount = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/demo/reset`, {}, { withCredentials: true, headers: getAuthHeaders() });
      toast.success('Demo account reset to $5,000!');
      setResetDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset account');
    } finally {
      setProcessing(false);
    }
  };

  const setTradeFromSignal = (signal) => {
    const entryParts = signal.entry_zone?.split('-') || [];
    const entry = entryParts[0]?.trim() || '';
    
    setNewTrade({
      market: signal.market || 'XAUUSD',
      direction: signal.direction?.toLowerCase() || 'buy',
      entry_price: entry,
      stop_loss: signal.stop_loss || '',
      take_profit: signal.take_profit?.split(',')[0]?.trim() || '',
      lot_size: '0.01',
      signal_id: signal.signal_id || ''
    });
    setOpenTradeDialog(true);
  };

  const calculateUnrealizedPnl = (trade) => {
    const currentPrice = prices[trade.market] || trade.current_price;
    const entry = trade.entry_price;
    const lotSize = trade.lot_size;
    const pipValue = 10 * lotSize * 100;
    
    if (trade.direction === 'buy') {
      return (currentPrice - entry) * pipValue;
    } else {
      return (entry - currentPrice) * pipValue;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto" />
          <p className="mt-4 text-slate-400">Loading Demo Trading...</p>
        </div>
      </div>
    );
  }

  // Show locked message when markets are open
  if (!demoAvailable) {
    return (
      <div className="space-y-6" data-testid="demo-trading-page">
        <Card className="bg-gradient-to-r from-amber-500/10 via-red-500/10 to-amber-500/10 border-amber-500/30 p-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center">
              <Lock className="w-8 h-8 text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-amber-400 mb-2">Demo Trading is Locked</h2>
              <p className="text-slate-300 mb-4">
                Markets are currently <span className="text-emerald-400 font-semibold">OPEN</span>! 
                Demo trading is only available when markets are closed (weekends).
              </p>
              <p className="text-slate-400 text-sm mb-4">
                Use this time to trade with real signals! Practice mode will be available again when markets close.
              </p>
              
              {marketStatus && (
                <div className="flex items-center gap-4 mt-6 p-4 bg-black/30 rounded-lg">
                  <Globe className="w-5 h-5 text-emerald-400" />
                  <div className="flex items-center gap-3">
                    {marketStatus.sessions && Object.entries(marketStatus.sessions).map(([session, info]) => (
                      <div key={session} className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${info.status === 'open' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`}></div>
                        <span className={`text-xs capitalize ${info.status === 'open' ? 'text-emerald-400' : 'text-slate-500'}`}>{session}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 mt-6">
                <Button onClick={() => navigate('/signals')} className="bg-emerald-600 hover:bg-emerald-700">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  View Live Signals
                </Button>
                <Button variant="outline" onClick={() => navigate('/trade-history')} className="border-zinc-700">
                  View Trade History
                </Button>
              </div>
            </div>
          </div>
        </Card>
        
        {/* Still show account stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-slate-900/50 border-white/5 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Balance</p>
                <p className="text-lg font-bold text-emerald-400">${account?.balance?.toLocaleString() || '5,000'}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-slate-900/50 border-white/5 p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${(account?.total_pnl || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'} rounded-lg flex items-center justify-center`}>
                <DollarSign className={`w-5 h-5 ${(account?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total P&L</p>
                <p className={`text-lg font-bold ${(account?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(account?.total_pnl || 0) >= 0 ? '+' : ''}${account?.total_pnl?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
          </Card>
          <Card className="bg-slate-900/50 border-white/5 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Win Rate</p>
                <p className="text-lg font-bold text-purple-400">{account?.win_rate || 0}%</p>
              </div>
            </div>
          </Card>
          <Card className="bg-slate-900/50 border-white/5 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Total Trades</p>
                <p className="text-lg font-bold">{account?.total_trades || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="demo-trading-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Demo Trading</h1>
          <p className="text-slate-400 mt-1">Practice trading with $5,000 virtual balance - No risk!</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setResetDialogOpen(true)}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            data-testid="reset-account-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset Account
          </Button>
          <Button 
            onClick={() => setOpenTradeDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="open-trade-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            Open Trade
          </Button>
        </div>
      </div>

      {/* Account Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Balance</p>
              <p className="text-lg font-bold text-emerald-400">${account?.balance?.toLocaleString() || '5,000'}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Equity</p>
              <p className="text-lg font-bold">${account?.equity?.toLocaleString() || '5,000'}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 ${(account?.total_pnl || 0) >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'} rounded-lg flex items-center justify-center`}>
              <DollarSign className={`w-5 h-5 ${(account?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
            </div>
            <div>
              <p className="text-xs text-slate-500">Total P&L</p>
              <p className={`text-lg font-bold ${(account?.total_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(account?.total_pnl || 0) >= 0 ? '+' : ''}${account?.total_pnl?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Win Rate</p>
              <p className="text-lg font-bold text-purple-400">{account?.win_rate || 0}%</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
              <Award className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Wins</p>
              <p className="text-lg font-bold text-emerald-400">{account?.winning_trades || 0}</p>
            </div>
          </div>
        </Card>
        
        <Card className="bg-slate-900/50 border-white/5 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Losses</p>
              <p className="text-lg font-bold text-red-400">{account?.losing_trades || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Trade from Signals */}
      {signals.length > 0 && (
        <Card className="bg-slate-900/50 border-white/5 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-emerald-400" />
            Quick Trade from Active Signals
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {signals.slice(0, 6).map((signal) => (
              <div 
                key={signal.signal_id}
                className="bg-slate-800/50 rounded-lg p-4 border border-white/5 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <Badge variant={signal.direction?.toLowerCase() === 'buy' ? 'default' : 'destructive'} className={signal.direction?.toLowerCase() === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                    {signal.direction?.toUpperCase()}
                  </Badge>
                  <span className="text-sm font-medium">{signal.market}</span>
                </div>
                <div className="text-sm text-slate-400 mb-3">
                  <p>Entry: {signal.entry_zone}</p>
                  <p>SL: {signal.stop_loss} | TP: {signal.take_profit?.split(',')[0]}</p>
                </div>
                <Button 
                  size="sm" 
                  className="w-full bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border border-emerald-500/30"
                  onClick={() => setTradeFromSignal(signal)}
                  data-testid={`trade-signal-${signal.signal_id}`}
                >
                  Trade This Signal
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Trades Tabs */}
      <Tabs defaultValue="open" className="w-full">
        <TabsList className="bg-slate-900/50 border border-white/5">
          <TabsTrigger value="open" className="data-[state=active]:bg-emerald-500/20">
            Open Positions ({openTrades.length})
          </TabsTrigger>
          <TabsTrigger value="closed" className="data-[state=active]:bg-slate-700">
            Trade History ({closedTrades.length})
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="open" className="mt-4">
          {openTrades.length === 0 ? (
            <Card className="bg-slate-900/50 border-white/5 p-8 text-center">
              <Play className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No open positions</p>
              <p className="text-sm text-slate-500 mt-1">Click "Open Trade" to start demo trading</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {openTrades.map((trade) => {
                const unrealizedPnl = calculateUnrealizedPnl(trade);
                const currentPrice = prices[trade.market] || trade.current_price;
                return (
                  <Card key={trade.trade_id} className="bg-slate-900/50 border-white/5 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${trade.direction === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                          {trade.direction === 'buy' ? 
                            <TrendingUp className="w-5 h-5 text-emerald-400" /> : 
                            <TrendingDown className="w-5 h-5 text-red-400" />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{trade.market}</span>
                            <Badge variant={trade.direction === 'buy' ? 'default' : 'destructive'} className={trade.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400 text-xs' : 'bg-red-500/20 text-red-400 text-xs'}>
                              {trade.direction.toUpperCase()}
                            </Badge>
                            <span className="text-xs text-slate-500">{trade.lot_size} lots</span>
                          </div>
                          <div className="flex gap-4 text-sm text-slate-400 mt-1">
                            <span>Entry: {trade.entry_price}</span>
                            <span>Current: {currentPrice}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-slate-500">P&L</p>
                          <p className={`font-bold ${unrealizedPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {unrealizedPnl >= 0 ? '+' : ''}${unrealizedPnl.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right text-sm text-slate-400">
                          <p>SL: {trade.stop_loss}</p>
                          <p>TP: {trade.take_profit}</p>
                        </div>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleCloseTrade(trade.trade_id)}
                          disabled={closingTradeId === trade.trade_id}
                          data-testid={`close-trade-${trade.trade_id}`}
                        >
                          {closingTradeId === trade.trade_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                          Close
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="closed" className="mt-4">
          {closedTrades.length === 0 ? (
            <Card className="bg-slate-900/50 border-white/5 p-8 text-center">
              <Clock className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No trade history</p>
              <p className="text-sm text-slate-500 mt-1">Your closed trades will appear here</p>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-500 border-b border-white/5">
                    <th className="pb-3 font-medium">Market</th>
                    <th className="pb-3 font-medium">Direction</th>
                    <th className="pb-3 font-medium">Entry</th>
                    <th className="pb-3 font-medium">Exit</th>
                    <th className="pb-3 font-medium">Lot Size</th>
                    <th className="pb-3 font-medium">P&L</th>
                    <th className="pb-3 font-medium">Result</th>
                    <th className="pb-3 font-medium">Closed</th>
                  </tr>
                </thead>
                <tbody>
                  {closedTrades.map((trade) => (
                    <tr key={trade.trade_id} className="border-b border-white/5">
                      <td className="py-3 font-medium">{trade.market}</td>
                      <td className="py-3">
                        <Badge variant={trade.direction === 'buy' ? 'default' : 'destructive'} className={trade.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400 text-xs' : 'bg-red-500/20 text-red-400 text-xs'}>
                          {trade.direction?.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="py-3 text-slate-400">{trade.entry_price}</td>
                      <td className="py-3 text-slate-400">{trade.exit_price}</td>
                      <td className="py-3 text-slate-400">{trade.lot_size}</td>
                      <td className={`py-3 font-medium ${(trade.realized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(trade.realized_pnl || 0) >= 0 ? '+' : ''}${trade.realized_pnl?.toFixed(2) || '0.00'}
                      </td>
                      <td className="py-3">
                        <Badge className={
                          trade.result === 'win' ? 'bg-emerald-500/20 text-emerald-400' :
                          trade.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-slate-400'
                        }>
                          {trade.result?.toUpperCase() || 'N/A'}
                          {trade.close_reason && ` (${trade.close_reason})`}
                        </Badge>
                      </td>
                      <td className="py-3 text-slate-500 text-sm">
                        {trade.closed_at ? new Date(trade.closed_at).toLocaleDateString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Open Trade Dialog */}
      <Dialog open={openTradeDialog} onOpenChange={setOpenTradeDialog}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle>Open Demo Trade</DialogTitle>
            <DialogDescription>
              Practice trading with virtual money. No real funds at risk.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Market</Label>
                <Select value={newTrade.market} onValueChange={(v) => setNewTrade({...newTrade, market: v})}>
                  <SelectTrigger className="bg-slate-800 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MARKETS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Direction</Label>
                <Select value={newTrade.direction} onValueChange={(v) => setNewTrade({...newTrade, direction: v})}>
                  <SelectTrigger className="bg-slate-800 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buy">
                      <span className="flex items-center gap-2">
                        <ChevronUp className="w-4 h-4 text-emerald-400" /> BUY
                      </span>
                    </SelectItem>
                    <SelectItem value="sell">
                      <span className="flex items-center gap-2">
                        <ChevronDown className="w-4 h-4 text-red-400" /> SELL
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Entry Price</Label>
              <Input 
                type="number"
                step="0.00001"
                value={newTrade.entry_price}
                onChange={(e) => setNewTrade({...newTrade, entry_price: e.target.value})}
                placeholder={prices[newTrade.market] ? `Current: ${prices[newTrade.market]}` : "Enter price"}
                className="bg-slate-800 border-white/10"
              />
              {prices[newTrade.market] && (
                <Button 
                  type="button"
                  variant="link"
                  size="sm"
                  className="text-xs text-emerald-400 p-0 h-auto mt-1"
                  onClick={() => setNewTrade({...newTrade, entry_price: prices[newTrade.market].toString()})}
                >
                  Use current price: {prices[newTrade.market]}
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Stop Loss</Label>
                <Input 
                  type="number"
                  step="0.00001"
                  value={newTrade.stop_loss}
                  onChange={(e) => setNewTrade({...newTrade, stop_loss: e.target.value})}
                  placeholder="SL price"
                  className="bg-slate-800 border-white/10"
                />
              </div>
              <div>
                <Label>Take Profit</Label>
                <Input 
                  type="number"
                  step="0.00001"
                  value={newTrade.take_profit}
                  onChange={(e) => setNewTrade({...newTrade, take_profit: e.target.value})}
                  placeholder="TP price"
                  className="bg-slate-800 border-white/10"
                />
              </div>
            </div>
            
            <div>
              <Label>Lot Size</Label>
              <Select value={newTrade.lot_size} onValueChange={(v) => setNewTrade({...newTrade, lot_size: v})}>
                <SelectTrigger className="bg-slate-800 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0.01">0.01 (Micro)</SelectItem>
                  <SelectItem value="0.05">0.05</SelectItem>
                  <SelectItem value="0.1">0.10 (Mini)</SelectItem>
                  <SelectItem value="0.5">0.50</SelectItem>
                  <SelectItem value="1.0">1.00 (Standard)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenTradeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleOpenTrade} 
              disabled={processing}
              className={newTrade.direction === 'buy' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-open-trade"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {newTrade.direction === 'buy' ? 'Open BUY' : 'Open SELL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Account Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="bg-slate-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              Reset Demo Account?
            </DialogTitle>
            <DialogDescription>
              This will reset your demo account to $5,000 and close all open positions. 
              Your trade history will be preserved but marked as cancelled.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetAccount} 
              disabled={processing}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reset-account"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset to $5,000
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
